/**
 * Browser capability detection for audio extraction strategies
 */

/**
 * Check if WebCodecs API is available
 */
export function isWebCodecsSupported(): boolean {
  return (
    typeof AudioDecoder !== 'undefined' && typeof AudioEncoder !== 'undefined'
  );
}

/**
 * Check if Opus encoding is supported via WebCodecs
 */
export async function isOpusEncoderSupported(): Promise<boolean> {
  if (!isWebCodecsSupported()) return false;

  try {
    const support = await AudioEncoder.isConfigSupported({
      codec: 'opus',
      sampleRate: 48000,
      numberOfChannels: 1,
      bitrate: 128000,
    });
    return support.supported === true;
  } catch {
    return false;
  }
}

/**
 * Check if AAC decoding is supported via WebCodecs
 */
export async function isAACDecoderSupported(): Promise<boolean> {
  if (!isWebCodecsSupported()) return false;

  try {
    // mp4a.40.2 is AAC-LC
    const support = await AudioDecoder.isConfigSupported({
      codec: 'mp4a.40.2',
      sampleRate: 44100,
      numberOfChannels: 2,
    });
    return support.supported === true;
  } catch {
    return false;
  }
}

/**
 * Check if SharedArrayBuffer is available (required for FFmpeg WASM)
 */
export function isSharedArrayBufferSupported(): boolean {
  return typeof SharedArrayBuffer !== 'undefined';
}

/**
 * Alias for backward compatibility
 */
export const isFFmpegSupported = isSharedArrayBufferSupported;

/**
 * Get comprehensive browser capabilities
 */
export interface BrowserCapabilities {
  webcodecs: boolean;
  opusEncoder: boolean;
  aacDecoder: boolean;
  sharedArrayBuffer: boolean;
  preferredStrategy: 'webcodecs' | 'ffmpeg' | 'passthrough';
}

export async function getBrowserCapabilities(): Promise<BrowserCapabilities> {
  const webcodecs = isWebCodecsSupported();
  const sharedArrayBuffer = isSharedArrayBufferSupported();

  let opusEncoder = false;
  let aacDecoder = false;

  if (webcodecs) {
    [opusEncoder, aacDecoder] = await Promise.all([
      isOpusEncoderSupported(),
      isAACDecoderSupported(),
    ]);
  }

  // Determine preferred strategy
  let preferredStrategy: BrowserCapabilities['preferredStrategy'];
  if (webcodecs && opusEncoder && aacDecoder) {
    preferredStrategy = 'webcodecs';
  } else if (sharedArrayBuffer) {
    preferredStrategy = 'ffmpeg';
  } else {
    preferredStrategy = 'passthrough';
  }

  return {
    webcodecs,
    opusEncoder,
    aacDecoder,
    sharedArrayBuffer,
    preferredStrategy,
  };
}
